import { FC, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Selector from '../../components/shared/Selector/Selector';
import Tabs from '../../components/shared/Tabs';
import { ServiceSupplier } from '../../services/service-supplier';
import { serviceAreas } from '../../services/service-areas';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import appState from '../../state/AppStateContainer';
import { alreadyUpToDateIdsOf, skippedIdsOf, SupplierBulkRunOutcome } from '../../util/supplierBulkOutcome';
import styles from './BulkManageCategoriesModal.module.scss';

type AreaOption = { value: string; label: string; __isNew__?: boolean };

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  areas: AreaOption[];
  supplierIds: string[];
  onSave: (outcome: SupplierBulkRunOutcome) => void;
  fetchAreas: () => Promise<void>;
  formatSupplierNames?: (ids: string[]) => string | null;
};

const BulkManageCategoriesModal: FC<Props> = ({ show, setShow, areas, supplierIds, onSave, fetchAreas, formatSupplierNames }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'bulkManageCategories' });
  // Creating a brand-new org-wide category (/areas/add) is admin-only; write-access
  // users (Inköp/Deltagare) can assign and remove existing categories.
  const canCreateAreas = appState.isAdminRole();
  const [selectedAreas, setSelectedAreas] = useState<AreaOption[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    setSelectedAreas([]);
  };

  // The creatable Selector surfaces a typed-but-unsaved entry as { __isNew__: true }.
  // Persist it as a real area, then swap the placeholder for the saved {value,label}.
  const handleChange = async (selected: AreaOption[]) => {
    const options = selected || [];
    const newOption = options.find(o => o.__isNew__);
    if (!newOption) {
      setSelectedAreas(options);
      return;
    }
    try {
      const updatedAreas = await serviceAreas.addArea({ name: newOption.label });
      await fetchAreas();
      const created = updatedAreas?.find((a: any) => a.name === newOption.label);
      const withoutNew = options.filter(o => !o.__isNew__);
      setSelectedAreas(created ? [...withoutNew, { value: created._id, label: created.name }] : withoutNew);
    } catch {
      setSelectedAreas(options.filter(o => !o.__isNew__));
      NotificationManager.error(
        t('notification.createError.description', 'Kunde inte skapa kategorin'),
        t('notification.createError.title', 'Något gick fel')
      );
    }
  };

  const handleSave = async () => {
    if (!selectedAreas.length) return;
    const areaIds = selectedAreas.map(a => a.value);
    setLoading(true);
    try {
      const serviceCall = tab === 0 ? ServiceSupplier.bulkAddAreas : ServiceSupplier.bulkRemoveAreas;
      const result = await serviceCall(supplierIds, areaIds);
      const changed = result?.updated ?? 0;
      const alreadyUpToDateIds = alreadyUpToDateIdsOf(result);
      const skippedIds = skippedIdsOf(result);
      if (changed > 0) {
        NotificationManager.success(
          t('notification.successCount', {
            count: changed,
            defaultValue_one: 'Kategorierna har uppdaterats för {{count}} leverantör',
            defaultValue_other: 'Kategorierna har uppdaterats för {{count}} leverantörer',
          }),
          t('notification.success.title', 'Sparat')
        );
      } else if (alreadyUpToDateIds.length === 0 && skippedIds.length === 0) {
        // Pre-contract server: no buckets to report — keep the old fixed toast rather
        // than a counted one asserting "för 0 leverantörer".
        NotificationManager.success(
          t('notification.success.description', 'Kategorierna har uppdaterats'),
          t('notification.success.title', 'Sparat')
        );
      }
      if (alreadyUpToDateIds.length > 0) {
        const names = formatSupplierNames?.(alreadyUpToDateIds) ?? null;
        const alreadyText = tab === 0
          ? (names
            ? t('notification.alreadyAdd.withNames', {
              count: alreadyUpToDateIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hade redan kategorierna: {{names}}',
              defaultValue_other: '{{count}} leverantörer hade redan kategorierna: {{names}}',
            })
            : t('notification.alreadyAdd.countOnly', {
              count: alreadyUpToDateIds.length,
              defaultValue_one: '{{count}} leverantör hade redan kategorierna',
              defaultValue_other: '{{count}} leverantörer hade redan kategorierna',
            }))
          : (names
            ? t('notification.alreadyRemove.withNames', {
              count: alreadyUpToDateIds.length,
              names,
              defaultValue_one: '{{count}} leverantör hade inte kategorierna: {{names}}',
              defaultValue_other: '{{count}} leverantörer hade inte kategorierna: {{names}}',
            })
            : t('notification.alreadyRemove.countOnly', {
              count: alreadyUpToDateIds.length,
              defaultValue_one: '{{count}} leverantör hade inte kategorierna',
              defaultValue_other: '{{count}} leverantörer hade inte kategorierna',
            }));
        NotificationManager.info(alreadyText);
      }
      if (skippedIds.length > 0) {
        const names = formatSupplierNames?.(skippedIds) ?? null;
        NotificationManager.info(names
          ? t('notification.skipped.withNames', {
            count: skippedIds.length,
            names,
            defaultValue_one: '{{count}} leverantör kunde inte uppdateras (borttagen eller otillgänglig): {{names}}',
            defaultValue_other: '{{count}} leverantörer kunde inte uppdateras (borttagna eller otillgängliga): {{names}}',
          })
          : t('notification.skipped.countOnly', {
            count: skippedIds.length,
            defaultValue_one: '{{count}} leverantör kunde inte uppdateras (borttagen eller otillgänglig)',
            defaultValue_other: '{{count}} leverantörer kunde inte uppdateras (borttagna eller otillgängliga)',
          }));
      }
      onSave({ changed, skippedIds });
      setShow(false);
      setSelectedAreas([]);
    } catch (e) {
      const errorKey = (e as any)?.response?.data?.error;
      const errorMsg = errorKey === 'some.areas.not.found'
        ? t('notification.error.areasNotFound', 'Någon av kategorierna finns inte längre. Ladda om sidan och försök igen.')
        : errorKey === 'error.bulkWriteFailed'
          ? t('notification.error.partialWrite', 'Något gick fel och vissa leverantörer kan ha uppdaterats. Ladda om sidan för att se vad som hände.')
          : errorKey ?? t('notification.error.description', 'Kunde inte uppdatera kategorierna');
      NotificationManager.error(errorMsg, t('notification.error.title', 'Något gick fel'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      setShow={(v: boolean) => { if (!v) setSelectedAreas([]); setShow(v); }}
      title={t('title', 'Hantera kategorier')}
      buttonInfo={{
        label: t('button.save', 'Spara'),
        action: handleSave,
        loaderShow: loading,
        disabled: selectedAreas.length === 0,
      }}
    >
      <div style={{ fontSize: '14px', color: 'var(--gray-700)', width: '500px', display: 'flex', gap: '16px', flexDirection: 'column' }}>
        <Tabs
          tabs={[t('tabs.add', 'Lägg till'), t('tabs.remove', 'Ta bort')]}
          selected={tab}
          onChange={handleTabChange}
        />
        <Selector
          className={styles.categoriesSelector}
          placeholder={t('placeholder.categories', 'Välj kategorier')}
          options={areas}
          value={selectedAreas}
          onChange={(selected: any) => handleChange(selected)}
          isMulti
          isCreatable={tab === 0 && canCreateAreas}
        />
      </div>
    </Modal>
  );
};

export default BulkManageCategoriesModal;
